/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  AppFormGroup,
  BuilderFormSchemaFactory,
  FormGroupSchemaConfig,
  FormRootSchema,
  FormState,
  FormStateSchema,
  updateChildrenSchema,
  whenAuOrNzRegion,
  whenAuRegion,
  whenNzRegion,
  whenUsRegion,
} from '@gh/builder-shared';
import {
  BusinessNumberType,
  ContactDeliveryMethod,
  ContactDetailsCreditorBusinessInfoDto,
  ContactDetailsCreditorDeliveryInfoDto,
  CreditorType,
  EmailMatch,
  isDeliveryMethodEmail,
  isDeliveryMethodMail,
  isWhtExemption,
  isWhtLiable,
  isWhtPayer,
  WithholdingTaxType,
} from '@gh/builder-shared-data';
import { getControl, getControlValue, getRootControl, setControlValue } from '@gh/core-ui';
import {
  ContactDetailsCreditorCommon,
  CreditorDestinationType,
} from '../../resources/model/contact-details-creditor.model';
import { DateTimeTypes, MaskedTypes, NumberTypes } from '@gh/common-shared';

export const getDestinationSchema = (deliveryInfo: ContactDetailsCreditorDeliveryInfoDto) => ({
  emailMatch: {
    required: true,
    visible: isDeliveryMethodEmail(deliveryInfo.deliveryMethod),
  },
  email: {
    required: true,
    enabled: deliveryInfo.emailMatch === EmailMatch.Different,
    visible: isDeliveryMethodEmail(deliveryInfo.deliveryMethod),
  },
  address: {
    enabled: false,
    visible: isDeliveryMethodMail(deliveryInfo.deliveryMethod),
  },
});

@Injectable()
export class ContactDetailsCreditorCommonFormState extends FormState<ContactDetailsCreditorCommon> {
  constructor(schemaFactory: BuilderFormSchemaFactory) {
    super(schemaFactory);
  }

  get whtExemption(): boolean {
    return this.group.value.businessInfo.type === WithholdingTaxType.WhtExemption;
  }

  get creditorType(): CreditorType {
    return getControlValue(this.group, 'type');
  }

  get rootForm(): FormGroup {
    return <FormGroup>getRootControl(this.group);
  }

  updateDestination(type: CreditorDestinationType): void {
    // update availability and visibility of destination controls
    const destination = <AppFormGroup>this.get(type);
    const destinationSchema = getDestinationSchema(destination.getRawValue());
    updateChildrenSchema(destination, destinationSchema || {});

    // update email field, if necessary
    const method = getControlValue(destination, 'deliveryMethod');
    const emailMatch = getControlValue(destination, 'emailMatch');

    if (method === ContactDeliveryMethod.Email && emailMatch !== EmailMatch.Different) {
      destination.patchValue({
        'email': getControlValue(this.rootForm, 'common.email'),
        'emailMatch': EmailMatch.Same,
      });
    }
  }

  updateMethod(type: CreditorDestinationType, method: ContactDeliveryMethod): void {
    this.updateDestination(type);
  }

  updateDifference(type: CreditorDestinationType, match: EmailMatch): void {
    this.updateDestination(type);

    const destination = <AppFormGroup>this.get(type);
    const method = getControlValue(destination, 'deliveryMethod');

    if (method === ContactDeliveryMethod.Email && match === EmailMatch.Different) {
      setControlValue(destination, 'email', '');
    }
  }

  updateEmail(): void {
    const defaultDeliveryToEmail = getControlValue(this.group, 'defaultDeliveryToEmail');
    const email = getControlValue(this.rootForm, 'common.email');

    ['orderBy', 'billingBy', 'scheduleBy']
      .map((destination) => <AppFormGroup>getControl(this.group, destination))
      .forEach((destination) => {
        const method = getControlValue(destination, 'deliveryMethod');
        const difference = getControlValue(destination, 'emailMatch');

        if (method === ContactDeliveryMethod.Email && difference === EmailMatch.Same) {
          // reset email if detination email should be the same
          destination.patchValue({ email });
        } else if (method === ContactDeliveryMethod.Mail && defaultDeliveryToEmail) {
          // reset to email method if current method is POST
          destination.patchValue({
            'deliveryMethod': ContactDeliveryMethod.Email,
            'emailMatch': EmailMatch.Same,
            email,
          });
        }

        // update availability and visibility of destination controls
        const destinationSchema = getDestinationSchema(destination.getRawValue());
        updateChildrenSchema(destination, destinationSchema || {});
      });
  }

  updateBusinessInfo(): void {
    const businessInfo = <AppFormGroup>this.get('businessInfo');
    updateChildrenSchema(businessInfo, this.getBusinessInfoSchemaConfig(businessInfo.value).children || {});
  }

  protected buildSchema(common: ContactDetailsCreditorCommon, { root, group }: FormStateSchema): FormRootSchema {
    return root({
      children: {
        defaultDeliveryToEmail: void 0,
        type: { required: true },
        orderBy: group({
          children: {
            deliveryMethod: { required: true },
            ...getDestinationSchema(common.orderBy),
          },
        }),
        billingBy: group({
          children: {
            deliveryMethod: { required: true },
            ...getDestinationSchema(common.billingBy),
          },
        }),
        scheduleBy: group({
          children: {
            deliveryMethod: { required: true },
            ...getDestinationSchema(common.scheduleBy),
          },
        }),
        businessInfo: group(this.getBusinessInfoSchemaConfig(common.businessInfo)),
        includeExtraDocs: void 0,
        excludeReceivingVendorReport: void 0,
        excludeReceivingOrders: void 0,
        provides: void 0,
        category: { required: true },
      },
    });
  }

  private getBusinessInfoSchemaConfig(businessInfo: ContactDetailsCreditorBusinessInfoDto): FormGroupSchemaConfig {
    const whtLiable = businessInfo.type ? isWhtLiable(businessInfo.type) : false;
    const whtPayer = businessInfo.type ? isWhtPayer(businessInfo.type) : false;
    const whtExemption = businessInfo.type ? isWhtExemption(businessInfo.type) : false;

    return {
      children: {
        // us region
        contractLicenseApplicable: void 0,
        numberType: { visible: whenUsRegion(), required: true },
        taxNumber: {
          visible: [whenUsRegion(), businessInfo.numberType === BusinessNumberType.Tax],
          type: MaskedTypes.TaxNumber,
        },
        socialNumber: {
          visible: [whenUsRegion(), businessInfo.numberType === BusinessNumberType.SecurityNo],
          type: MaskedTypes.SocialNumber,
        },
        eligibleFor1099Form: { visible: whenUsRegion() },
        form1099name: { visible: whenUsRegion() },
        // au region
        licenseNumber: { visible: [!!businessInfo.contractLicenseApplicable] },
        licenceExpired: { type: DateTimeTypes.ShortDate },
        // au region
        abn: { visible: whenAuRegion(), type: MaskedTypes.Abn },
        // au & nz region
        activity: { visible: whenAuOrNzRegion(), required: whenNzRegion() },
        // nz region
        type: { visible: whenNzRegion(), required: true },
        gst: { visible: [whenNzRegion(), whtLiable], required: true, type: MaskedTypes.Gst },
        defWhtRate: {
          visible: [whenNzRegion(), whtPayer],
          enabled: false,
          type: NumberTypes.Price,
        },
        taxRate: { visible: [whenNzRegion(), whtPayer || whtExemption], required: true, type: NumberTypes.Price },
        certificateNbr: { visible: [whenNzRegion(), whtExemption], required: true },
        certificateDate: { visible: [whenNzRegion(), whtExemption], required: true, type: DateTimeTypes.ShortDate },
      },
    };
  }
}
